import { GetterTree } from "vuex";
import { RootState } from "../types";
import { Grouping, RuleActionPlan, RuleDropdownOptions, RulesEngineState, RuleServerType } from "./types";
import RulesText from "@/components/rules-engine-setup/RulesText.vue";
import RulesLogicBuilder from "@/components/rules-engine-setup/RulesLogicBuilder.vue";
import RulesScope from "@/components/rules-engine-setup/RulesScope.vue";
import RulesNumberInput from "@/components/rules-engine-setup/data-components/RulesNumberInput.vue";
import RulesAlarmDropdown from "@/components/rules-engine-setup/data-components/RulesAlarmDropdown.vue";
import RulesTimeDurationInput from "@/components/rules-engine-setup/data-components/RulesTimeDurationInput.vue";
import ParkActionOptionsInput from "@/components/rules-engine-setup/data-components/ParkActionOptionsInput.vue";
import { ComponentToValue, CardComponent } from "@/store/rules-engine/types";
import RuleGenericDropdown from "@/components/rules-engine-setup/data-components/RuleGenericDropdown.vue";
import { get } from "lodash";
import { isRegexValid } from "@/utils/string-utils";
import { TextValidationResult } from "@/types/text-validation-result";
import { RulesEngineConstants } from "@/types/Constants"
import RulesCheckbox from "@/components/rules-engine-setup/data-components/RulesCheckbox.vue";
import RuleAlarmTag, { RuleAlarmTagAction } from "@/components/rules-engine-setup/data-components/RuleAlarmTag.vue";

const minDurationSeconds: number = 1;
const maxDurationSeconds: number = 30;
const maxDurationSecondsExtended: number = 1800;

const placeholderPrompt: string = `Enter a value between ${minDurationSeconds} and ${maxDurationSeconds}`;
const placeholderPromptExtended: string = `Enter a duration up to ${maxDurationSecondsExtended / 60} mins`;

export const getters: GetterTree<RulesEngineState, RootState> = {
	getTriggerType(): any[] {
        return [
            {
                id: 1,
                title: "Device Type"
            },
            {
                id: 2,
                title: "Alarm Type"
            },
            {
                id: 3,
                title: "Received After X Seconds"
            }
        ];
    },
	getActionType(): any[] {
        return [
            {
                id: 1,
                title: "Raise Priority"
            },
            {
                id: 2,
                title: "Action Plan"
            },
            {
                id: 3,
                title: "Combine Alarms"
            }
        ];
    },
	getScopeCriteria(): any[] {
        return [
            {
                id: 1,
                title: "Device Type"
            },
            {
                id: 2,
                title: "Alarm Type"
            }
        ];
    },
	getOperators(): string[] {
		return ["AND", "OR"];
	},
	getComponentLinks: (state, rootState, rootGetters) =>  {
		const componentLinks = getDefaultComponentLinks(state, rootGetters);

		// Add ActionTypeRegex action
		addActionTypeRegexActionToComponentLinks(componentLinks, rootGetters);

		// Add ActionTypePark action
		addActionTypeParkActionToComponentLinks(componentLinks, rootGetters);

		// Add EventDetails action
		addDetailsContainsComponentLinks(componentLinks, rootGetters);

		// Add TriggerTypePriority triggers
		addAlarmPriorityComponentLinks(componentLinks, rootGetters);

		addAlarmTagLinks(componentLinks, rootGetters);

		return componentLinks;
	},
	getTitleComponentConfig(): CardComponent
    {
        return {
            component: RulesText,
            props: {
                fieldName: "Title"
            }
        }
    },
    getActionBuilderConfig(): CardComponent
    {
        return {
            component: RulesLogicBuilder,
            props: {
                section: "Action",
                rootText: "Action",
                allowedLogic: {
                    allowAnd: true,
                    allowOr: false,
                    subRules:
                    {
                        allowAnd: false,
                        allowOr: false
                    }
                },
            }
        }
    },
    getScopeBuilderConfig(): CardComponent
    {
        return {
            component: RulesScope,
            props: {
                section: "Scope",
                rootText: "Scope",
                allowedLogic: {
                    allowAnd: true,
                    allowOr: false,
                    subRules:
                    {
                        allowAnd: false,
                        allowOr: false
                    }
                },
            }
        }
    },
    getTriggerBuilderConfig(): CardComponent
    {
        return {
            component: RulesLogicBuilder,
            props: {
                section: "Trigger",
                rootText: "Trigger",
                allowedLogic: {
                    allowAnd: true,
                    allowOr: true,
                    subRules:
                    {
                        allowAnd: true,
                        allowOr: true
                    }
                },
            }
        }
    },
	getGrouping(): Grouping[] {
		return [
			{
				id: 1,
				title: "By Alarm"
			}, {
				id: 2,
				title: "By Area"
			}
		];
	}
}

const getDefaultComponentLinks = (state: RulesEngineState, rootGetters) => {
	const timeExtensionFeature = get(rootGetters.featuresList, ["RulesEngine", "30MinuteExtension"]);
	const eventCloseEventContainsOnlyScopeAndTriggerFeature = get(rootGetters.featuresList, ["RulesEngine", "EventCloseEventContainsOnlyScopeAndTrigger"]);

	return [
		{
			component: RulesText,
			componentFor: ["Scope","Trigger","Action"],
			valueComponents:[
				{
					section: "Scope",
					valueComponentLinks:
					[
						{
							value: 1, // Device Type
							component: RuleGenericDropdown,
							props: {
								options: state.serverTypes.map((s: RuleServerType) => {
									return { id: s.serverTypeId, title: s.title }
								}),
								allowNull: false,
								placeholderText: "Select a Device Type"
							}
						} as ComponentToValue,
						{
							value: 2, // Alarm Type
							component: RulesAlarmDropdown,
							props: {
								allowNulls: false
							}
						} as ComponentToValue,
					]
				},
				{
					section: "Action",
					valueComponentLinks:
					[
						{
							value: 1, // Raise Priority
							component: RulesNumberInput,
							props: {
								allowNulls: false,
								minValue: 1,
								maxValue: Number.MAX_VALUE,
								placeholderText: "Enter a Priority"
							}
						} as ComponentToValue,
						{
							value: 2, // Action Plan Dropdown
							component: RuleGenericDropdown,
							props: {
								options: state.actionsPlans.map((s: RuleActionPlan) => {
									return { id: s.actionPlanId, title: s.title }
								}),
								allowNull: false,
								placeholderText: "Select an Action Plan"
							}
						} as ComponentToValue,
						{
							value: 3, // Combine Alarms
							component: null
						} as ComponentToValue,
						{
							value: 4, // Close Event
							component: eventCloseEventContainsOnlyScopeAndTriggerFeature ? RulesCheckbox : null,
							props: eventCloseEventContainsOnlyScopeAndTriggerFeature ? {
								allowNulls: true,
								placeholderText: "Event contains only Scope and Trigger",
								toolTipText: "Only close events that contain only Scope and Trigger steps.",
								propertyName: "eventContainsOnlyScopeAndTrigger"
							} : {}
						} as ComponentToValue
					]
				},
				{
					section: "Trigger",
					valueComponentLinks:
					[
						{
							value: 1, // Device Type
							component: RuleGenericDropdown,
							props: {
								options: state.serverTypes.map((s: RuleServerType) => {
									return { id: s.serverTypeId, title: s.title }
								}),
								allowNull: false,
								placeholderText: "Select a Device Type"
							}
						} as ComponentToValue,
						{
							value: 2, // Alarm Type
							component: RulesAlarmDropdown,
							props: {
								allowNulls: false
							}
						} as ComponentToValue,
						{
							value: 3, // Received After
							component: RulesTimeDurationInput,
							props: {
								allowNulls: false,
								minValue: 1,
								maxValue: timeExtensionFeature ? maxDurationSecondsExtended : maxDurationSeconds,
								placeholderText: timeExtensionFeature ? placeholderPromptExtended : placeholderPrompt,
								placeholderTextMinutes: "Minutes",
								placeholderTextSeconds: "Seconds"
							}
						} as ComponentToValue,
						{
							value: 4,
							component: RuleGenericDropdown,
							props: {
								options: null,
								ruleDropdownOption: RuleDropdownOptions.server,
								allowNull: false,
								placeholderText: "Select a Device"
							}
						} as ComponentToValue,
						{
							value: 5,
							component: RuleGenericDropdown,
							props: {
								options: null,
								ruleDropdownOption: RuleDropdownOptions.serverTypeEvent,
								allowNull: false,
								placeholderText: "Select an Event Type"
							}
						} as ComponentToValue,
						{
							value: 6,
							component: RulesNumberInput,
							props: {
								allowNulls: false,
								minValue: -1,
								placeholderText: "Input 1"
							}
						} as ComponentToValue,
						{
							value: 7,
							component: RulesNumberInput,
							props: {
								allowNulls: false,
								minValue: -1,
								placeholderText: "Input 2"
							}
						} as ComponentToValue,
						{
							value: 8,
							component: RulesTimeDurationInput,
							props: {
								allowNulls: false,
								minValue: 1,
								maxValue: timeExtensionFeature ? maxDurationSecondsExtended : maxDurationSeconds,
								placeholderText: timeExtensionFeature ? placeholderPromptExtended : placeholderPrompt,
								placeholderTextMinutes: "Minutes",
								placeholderTextSeconds: "Seconds"
							}
						} as ComponentToValue
					]
				}
			]
		}
	];
}

const addActionTypeRegexActionToComponentLinks = (componentLinks, rootGetters) => {

	const actionPlanRegexFeatureEnabled = get(rootGetters.featuresList, ["RulesEngine", "ActionPlanRegex"]);

	if (actionPlanRegexFeatureEnabled) {
		componentLinks.forEach(c => c.valueComponents.forEach(vc => {

		if (vc.section === "Action") {
			const section = vc.valueComponentLinks;
			section.push({
				value: 5,
				component: RulesText,
				props: {
					validation: (input: string): TextValidationResult => isRegexValid(input)
				}
			} as ComponentToValue);
		}
		}));
	}
}

const addActionTypeParkActionToComponentLinks = (componentLinks, rootGetters) => {
	const actionPlanParkFeatureEnabled = get(rootGetters.featuresList, ["RulesEngine", "ParkAction"]);
	let timeExtensionFeature = get(rootGetters.featuresList, ["RulesEngine", "30MinuteExtension"]);

	if (actionPlanParkFeatureEnabled) {
		componentLinks.forEach(c => c.valueComponents.forEach(vc => {
			if (vc.section === "Action") {
				const section = vc.valueComponentLinks;
				section.push({
					value: 6,
					component: ParkActionOptionsInput,
					props: {
						allowNulls: false,
						minValue: 1,
						maxValue: Number.MAX_VALUE,
						minDurationValue: 1,
						maxDurationValue: timeExtensionFeature ? maxDurationSecondsExtended : maxDurationSeconds,
						placeholderText: "Maximum park count",
						placeholderTextMinutes: "Minutes",
						placeholderTextSeconds: "Seconds",
						dataCyForPark: "rules-park-action-duration",
						dataCyForMinutes: "rules-park-action-minutes",
						dataCyForSeconds: "rules-park-action-seconds"
					}
				} as ComponentToValue);
			}
		}));
	}
}

const addDetailsContainsComponentLinks = (componentLinks, rootGetters) => {

	const RulesEngineEventDetailsFeatureEnabled = get(rootGetters.featuresList, ["RulesEngine", "IncludeScopeAsTrigger", "DetailsRegexMatching"]);

	if (RulesEngineEventDetailsFeatureEnabled) {
		componentLinks.forEach(c => c.valueComponents.forEach(vc => {

			if (vc.section === "Trigger") {
				const section = vc.valueComponentLinks;
				section.push({
					value: 9,
					component: RulesText,
					props: {
						validation: (input: string): TextValidationResult => isRegexValid(input),
						allowNulls: false,
						placeholderText: "Add Regex for text match",
					}
				} as ComponentToValue);
			}
		}));
	}
}

const addAlarmPriorityComponentLinks = (componentLinks, rootGetters) => {
	const triggerPriorityEnabled = get(rootGetters.featuresList, ["RulesEngine", "IncludeScopeAsTrigger", "AlarmPriorityTrigger"]);

	if (triggerPriorityEnabled) {
		componentLinks.forEach(c => c.valueComponents.forEach(vc => {
			if (vc.section === "Trigger") {
				const section = vc.valueComponentLinks;

				section.push({
					value: RulesEngineConstants.TRIGGER_PRIORITY_LESS_THAN_EQUALS_ID,
					component: RulesNumberInput,
					props: {
						allowNulls: false,
						minValue: 0,
						maxValue: Number.MAX_VALUE,
						placeholderText: "Enter a Priority"
					}
				} as ComponentToValue);

				section.push({
					value: RulesEngineConstants.TRIGGER_PRIORITY_GREATER_THAN_EQUALS_ID,
					component: RulesNumberInput,
					props: {
						allowNulls: false,
						minValue: 0,
						maxValue: Number.MAX_VALUE,
						placeholderText: "Enter a Priority"
					}
				} as ComponentToValue);
			}
		}));
	}
}

const addAlarmTagLinks = (componentLinks, rootGetters) => {
	const tagsEnabled = get(rootGetters.featuresList, ["Alarms", "AlarmTags"]);

	if (tagsEnabled && get(rootGetters.featuresList, ["RulesEngine", "AddAlarmTagAction"])) {
		componentLinks.forEach(c => c.valueComponents.forEach(vc => {
			if (vc.section === "Action") {
				const section = vc.valueComponentLinks;

				section.push({
					value: 7,
					component: RuleAlarmTag,
					props: {
						action: RuleAlarmTagAction.Add,
					}
				} as ComponentToValue);
			}
		}));
	}

	if (tagsEnabled && get(rootGetters.featuresList, ["RulesEngine", "RemoveAlarmTagAction"])) {
		componentLinks.forEach(c => c.valueComponents.forEach(vc => {
			if (vc.section === "Action") {
				const section = vc.valueComponentLinks;

				section.push({
					value: 8,
					component: RuleAlarmTag,
					props: {
						action: RuleAlarmTagAction.Remove,
					}
				} as ComponentToValue);
			}
		}));
	}
}